import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, Droplets, Trash2, Minus, Plus, ShoppingBag, ArrowRight,
  Award, Truck, Clock
} from 'lucide-react';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, loading, fetchCart, updateCartItem, clearCart } = useCart();

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleUpdateQuantity = async (productId, newQuantity) => {
    await updateCartItem(productId, newQuantity);
  };

  const handleClearCart = async () => {
    await clearCart();
    toast.success('Cesto limpo');
  };

  const pointsToEarn = Math.floor((cart.total_aoa || 0) / 1000);

  return (
    <div className="min-h-screen bg-zinc-100 pb-36">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-zinc-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-barlow font-bold text-xl">Cesto de Compras</h1>
            {cart.items.length > 0 && (
              <p className="text-xs text-zinc-500">{cart.items.length} {cart.items.length === 1 ? 'produto' : 'produtos'}</p>
            )}
          </div>
          {cart.items.length > 0 && (
            <button
              onClick={handleClearCart}
              className="tap-target px-3 py-3 text-red-500 text-sm font-medium hover:text-red-600"
              data-testid="clear-cart-btn"
            >
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Cart Items */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl h-28 animate-pulse"></div>
            ))}
          </div>
        ) : cart.items.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-12 h-12 text-zinc-300" />
            </div>
            <h2 className="font-barlow font-bold text-xl text-zinc-800 mb-2">Cesto Vazio</h2>
            <p className="text-zinc-500 font-manrope mb-6 max-w-xs mx-auto">
              Ainda não adicionou nenhum produto. Explore o nosso catálogo de lubrificantes!
            </p>
            <Link to="/catalog">
              <Button className="bg-sonangol-red hover:bg-red-700" data-testid="browse-products-btn">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Explorar Produtos
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {cart.items.map((item) => (
              <div 
                key={item.product.id} 
                className="bg-white rounded-xl p-4 shadow-sm border border-zinc-100"
                data-testid={`cart-item-${item.product.id}`}
              >
                <div className="flex gap-4">
                  <div className="w-20 h-20 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Droplets className="w-10 h-10 text-sonangol-red" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-barlow font-bold text-zinc-800">{item.product.name}</h3>
                        <p className="text-xs text-zinc-500 font-manrope">
                          {item.product.viscosity} · {item.product.volume_liters}L
                        </p>
                      </div>
                      <button
                        onClick={() => handleUpdateQuantity(item.product.id, 0)}
                        className="w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`remove-item-${item.product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center bg-zinc-100 rounded-lg">
                        <button
                          onClick={() => handleUpdateQuantity(item.product.id, item.quantity - 1)}
                          className="w-9 h-9 flex items-center justify-center text-zinc-600 hover:bg-zinc-200 rounded-l-lg transition-colors"
                          data-testid={`decrease-${item.product.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-center font-barlow font-bold">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(item.product.id, item.quantity + 1)}
                          className="w-9 h-9 flex items-center justify-center text-zinc-600 hover:bg-zinc-200 rounded-r-lg transition-colors"
                          data-testid={`increase-${item.product.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="font-barlow font-bold text-lg text-sonangol-red">
                        {item.subtotal.toLocaleString('pt-AO')} <span className="text-sm">AOA</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Benefits Banner */}
            <div className="bg-gradient-to-r from-sonangol-red/10 to-sonangol-orange/10 rounded-xl p-4 border border-sonangol-red/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sonangol-red rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-barlow font-bold text-zinc-800">+{pointsToEarn} pontos de fidelidade</p>
                  <p className="text-xs text-zinc-500">Acumulará com esta compra</p>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="flex gap-3">
              <div className="flex-1 bg-white rounded-xl p-3 border border-zinc-100 flex items-center gap-3">
                <Truck className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500">Entrega</p>
                  <p className="font-barlow font-semibold text-zinc-800 text-sm">Disponível</p>
                </div>
              </div>
              <div className="flex-1 bg-white rounded-xl p-3 border border-zinc-100 flex items-center gap-3">
                <Clock className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500">Tempo estimado</p>
                  <p className="font-barlow font-semibold text-zinc-800 text-sm">30-60 min</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Bar */}
      {cart.items.length > 0 && (
        <div className="fixed above-nav left-0 right-0 bg-white border-t border-zinc-200 px-4 py-4 z-30 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-zinc-500 text-sm">Total</span>
              <p className="font-barlow font-bold text-2xl text-zinc-800">
                {cart.total_aoa.toLocaleString('pt-AO')} <span className="text-sm font-normal text-zinc-500">AOA</span>
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                +{pointsToEarn} pontos
              </span>
            </div>
          </div>
          <Button
            className="w-full h-12 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider"
            onClick={() => navigate('/checkout')}
            data-testid="checkout-btn"
          >
            Finalizar Compra
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default CartPage;
